/**
 * PerformanceTab — the per-study analytics view on the Study Hub. Reuses the
 * Reports components, pinned to this study (studyId scopes them by the lead's
 * current primary study). Has its own range selector (study-scoped pref keys so
 * it never collides with the global Reports page state). Mounted ONLY when the
 * Performance tab is active (StudyDetail otherwise keeps tabs mounted-but-hidden;
 * conditional mount here avoids firing these fetches in the background).
 */
import { useMemo, useState } from 'react';
import { getPref, setPref, rangeWindow } from '../../lib';
import MetricsReport from '../reports/MetricsReport';
import StageConversionReport from '../reports/StageConversionReport';
import FunnelReport from '../reports/FunnelReport';
import DQReport from '../reports/DQReport';
import ProductivityReport from '../reports/ProductivityReport';
import AdSpendStudySection from '../reports/AdSpendStudySection';

const RANGES = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '180', label: 'Last 180 days' },
  { value: 'all', label: 'All time' },
] as const;

const selectCls =
  'rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';

function Section({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className={`text-sm font-semibold text-gray-900 dark:text-gray-100 ${subtitle ? '' : 'mb-2'}`}>{title}</h3>
      {subtitle && <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>}
      {children}
    </section>
  );
}

export default function PerformanceTab({ studyId }: { studyId: string }) {
  const [range, setRange] = useState(() => (RANGES.some((r) => r.value === getPref('reports.study.range', '90')) ? getPref('reports.study.range', '90') : '90'));
  const { from, to } = useMemo(() => rangeWindow(range), [range]);
  const setR = (v: string) => {
    setRange(v);
    setPref('reports.study.range', v);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-gray-500 dark:text-gray-400">Recruitment performance for this study.</p>
        <select value={range} onChange={(e) => setR(e.target.value)} aria-label="Time range" className={selectCls}>
          {RANGES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
      </div>

      <Section title="Sales metrics">
        <MetricsReport from={from} to={to} studyId={studyId} />
      </Section>
      {/* These two panels are BOTH reached-stage funnels since 20260818140000,
          and they sit inches apart over the same study and range. They will not
          always agree, because they cohort differently on purpose — so each one
          says which cohort it is counting rather than leaving the reader to
          discover the disagreement. "Current pipeline" was an accurate title
          when the Funnel bars were occupancy; it is not one now. */}
      <Section
        title="Conversion"
        subtitle="Counts leads by when they entered the pipeline, and adds stage-to-stage rates and time in stage."
      >
        <StageConversionReport groupBy="overall" from={from} to={to} studyId={studyId} />
      </Section>
      <Section
        title="Funnel"
        subtitle="Counts leads by when they were created. Bars are everyone who ever reached a stage; “here now” is who is sitting there today."
      >
        <FunnelReport groupBy="overall" from={from} to={to} studyId={studyId} />
      </Section>
      <Section title="Disqualifications">
        <DQReport groupBy="category" from={from} to={to} studyId={studyId} />
      </Section>
      {/* Plan §7.5. Admin-gated INSIDE the component (this tab is not admin-only),
          and it renders an explicit "nothing is mapped" state rather than hiding:
          a study whose ads are unmapped must look unmapped, not unadvertised. */}
      <AdSpendStudySection studyId={studyId} from={from} to={to} />
      <Section title="Recruiter productivity">
        <ProductivityReport from={from} to={to} studyId={studyId} />
      </Section>
    </div>
  );
}
